import { useCallback, useContext, useMemo } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Box, PropertyCard, Tag, Text } from '@zillow/constellation-10';
import { IconHomesFilled, IconWarningFilled } from '@zillow/constellation-icons';

import { AccountViewListingStatus } from '@zg-rentals/rental-manager-env';

import { RMWContext } from '../../../../providers/RMWContext';

import PropertiesActions from '../../../../actions/PropertiesActions';

import { useSelector } from '../../../../selectors/useSelector';

import { getDashboardOrSOHPath } from '../../../../utils/pathUtils';

import propertyUtils from '../../../../utils/propertyUtils';
import { getTagTypeByKey, STATUS_KEY, type TagTone } from '../../../../utils/statusTagUtils';

import PropertiesPageCSEvents from '../../../../model/ClickstreamEvent/PropertiesPageCSEvents';

import MidwestStyleHouse2x from '../../../../images/MidwestStyleHouse@2x.svg';
import { fallbackPhotoStyles } from './styles';

import type { MouseEvent } from 'react';
import type { PhotoUrlsProps, PropertyProps } from '../../../../model/Property';

type Props = {
  listing: PropertyProps;
};

const PropertyCardSimple = ({
  listing: { isMultifamily, address, photoUrls, status, listingAlias },
}: Props): JSX.Element => {
  const { paths } = useContext(RMWContext);
  const dispatch = useDispatch();

  const { canAccessDashboardRedesign, user } = useSelector(
    (state) => ({
      canAccessDashboardRedesign: state.app.canAccessDashboardRedesign,
      user: state.user,
    }),
    shallowEqual,
  );

  const { formattedCityStateZip, formattedCompleteAddress, formattedName, formattedStreet } = useMemo(() => {
    return propertyUtils.formatAddress(address);
  }, [address]);

  // NOTE: `handleOnClick` also handles `ENTER` and `SPACE` keyUp
  const handleOnClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      PropertiesPageCSEvents.PropertyCardClick({ user });
      dispatch(PropertiesActions.updateCurrentProperty({ ...address }));

      /**
       * since isMultifamily is a prop passed in
       * the canAccessDashboardRedesign is out of sync until a few rerenders later
       * we need to pass isMultifamily to getDashboardOrSOHPath
       **/
      const pathToRoute = getDashboardOrSOHPath({
        canAccessDashboardRedesign,
        isMultifamily,
        listingAlias,
        propertiesPage: paths?.propertiesPage,
      });

      if (e.metaKey || e.ctrlKey) {
        window.router.updateLocation(pathToRoute, '_blank');

        return;
      }

      window.router.transitionTo(pathToRoute);
    },
    [listingAlias],
  );

  const getTagIcon = () => {
    if (status.status === AccountViewListingStatus.UNABLE_TO_PUBLISH) {
      return <IconWarningFilled />;
    }
    if (isMultifamily) {
      return <IconHomesFilled />;
    }
    return null;
  };

  return (
    <Box data-testid="PropertyCardSimple" css={{ flex: '1 1 30%' }}>
      <PropertyCard
        aria-label={formattedCompleteAddress}
        data-eventid={PropertiesPageCSEvents.PropertyCardClick.data_event_id}
        data={{
          dataArea1: (
            <Text textStyle="heading-sm" asChild css={{ fontFamily: 'heading-alt' }}>
              <h4>{formattedName ? formattedName : formattedStreet}</h4>
            </Text>
          ),
          dataArea2: formattedCityStateZip,
          dataArea3: (
            <Tag
              tone={
                status.tagAppearance === 'error'
                  ? 'critical'
                  : (status.tagAppearance as TagTone) ||
                    getTagTypeByKey(STATUS_KEY.LISTING_DISPLAY_STATUS, status.status)
              }
              icon={getTagIcon()}
              css={{ marginRight: '200', textTransform: 'uppercase' }}
            >
              {status.text}
            </Tag>
          ),
        }}
        photoBody={
          (photoUrls as PhotoUrlsProps)?.medium ? (
            <PropertyCard.Photo src={photoUrls.medium} alt={formattedCompleteAddress} />
          ) : (
            <Box role="img" css={fallbackPhotoStyles} style={{ backgroundImage: `url(${MidwestStyleHouse2x})` }} />
          )
        }
        onClick={handleOnClick}
        // eslint-disable-next-line curly-quotes/no-straight-quotes
        css={{ maxWidth: '100%', '& [data-c11n-component="PropertyCard.DataWrapper"]': { padding: '400' } }}
      />
    </Box>
  );
};

export default PropertyCardSimple;
